// src/pages/KnowledgeBase/FAQ.jsx

import React from 'react';
import Page from 'components/Page';
import SectionCard from 'components/SectionCard';

export default function FAQ() {
  const sections = [
    {
      title: 'General',
      items: [
        {
          q: 'Does MarketMap require access?',
          a: 'No, it is available to all users.',
        },
        {
          q: 'How do I get access to paid apps?',
          a: 'Paid apps are available to subscribing customers. If you’re interested, please contact your sales representative or visit the Contact Us page.',
        },
      ],
    },

    {
      title: 'Market Data',
      items: [
        {
          q: 'Is the market data real-time?',
          a: 'No. Compass provides end-of-day market data, updated after market close when applicable. Valuations may be given for non-business days for accrual purposes. Data on this system is not presented to the same degree of accuracy as real-time quotations.',
        },
        {
          q: 'How is the market data sourced and displayed?',
          a: 'Data comes from sources believed to be reliable and may be adjusted, normalized, or rounded for consistent display across charts and tables.',
        },
        {
          q: 'Why might Compass data differ from other price sources?',
          a: 'Differences can occur due to timing (end-of-day vs. intraday), unit conversions, rounding, or local conventions. Market data in Compass is for informational and analytical purposes only, not for trade execution.',
        },
      ],
    },

    {
      title: 'TradeTracker',
      items: [
        {
          q: 'Where do the values in TradeTracker come from?',
          a: 'TradeTracker valuations are generated using proprietary models and assumptions. They are not sourced from exchange-quoted prices or third-party vendors.',
        },
        {
          q: 'How are valuations calculated for a given day?',
          a: 'All trades are valued against a uniform market snapshot for that date. The same market curves, data inputs, and assumptions are applied consistently across the portfolio.',
        },
        {
          q: 'Why might valuations differ from dealer marks or other systems?',
          a: 'OTC derivatives can be valued using different methodologies, curve sets, or market inputs. As a result, TradeTracker valuations may differ from dealer marks or other analytics platforms.',
        },
        {
          q: 'Can I see the details of Greenland’s valuation methodology?',
          a: 'While the full model design is not disclosed, we provide support to ensure transparency where it matters. We work with organizations, auditors, and accountants to explain key inputs, modeling assumptions, and valuation logic so they can evaluate reasonableness.',
        },
      ],
    },

    {
      title: 'DebtStudio',
      items: [
        {
          q: 'Why might DebtStudio results differ from another financing model or system?',
          a: 'Differences can occur due to day count conventions, principal period assumptions, yield interpolation methods, rounding behavior, call assumptions, timing conventions, pricing inputs, or financing cost treatment. Different systems may also apply different methodologies for statistics such as TIC, NIC, AIC or arbitrage yield.',
        },
        {
          q: 'Does DebtStudio support monthly or commercial-style amortization?',
          a: 'Yes. DebtStudio supports structures based on principal periods rather than only annual bond years. Principal frequencies may include annual, semiannual, quarterly, monthly and other supported intervals.',
        },
        {
          q: 'How are DebtStudio scales generated?',
          a: 'DebtStudio scales may be constructed using observable individual prices, benchmark market curves, interpolation methods and spread adjustments. Users may edit coupons, spreads and pricing assumptions directly within the Scale Builder.',
        },
        {
          q: 'What interpolation methods are supported in DebtStudio?',
          a: 'DebtStudio supports interpolation methods including linear interpolation and cubic spline interpolation for benchmark curve construction and financing scale generation.',
        },
        {
          q: 'Why might DebtStudio prices or yields differ from another source?',
          a: 'Differences may result from benchmark curve selection, interpolation methodology, spread assumptions, settlement assumptions, call assumptions, day count conventions, rounding conventions or timing differences in market data.',
        },
        {
          q: 'What does “wrapped debt service” mean?',
          a: 'Wrapped debt service refers to a structure where new debt service is designed in consideration of existing debt service obligations so aggregate cash flows follow a desired repayment pattern.',
        },
      ],
    },

    {
      title: 'FundPlanner',
      items: [
        {
          q: 'How does FP Structurer build an investment solution?',
          a: 'FP Structurer attempts to generate an investment allocation that satisfies the required future cash flow schedule using available securities and structuring assumptions.',
        },
        {
          q: 'What is the objective of the FP Structurer optimization?',
          a: 'FundPlanner generally uses a cost minimization approach, attempting to identify the lowest cost portfolio of investments that satisfies the required cash flow schedule subject to applicable assumptions and constraints.',
        },
        {
          q: 'Why might FundPlanner results differ from another portfolio or escrow model?',
          a: 'Differences may occur due to security availability, yield assumptions, settlement assumptions, maturity handling, cash flow timing, optimization settings, rounding behavior or valuation methodology.',
        },
        {
          q: 'What does Cashflow Sufficiency mean?',
          a: 'Cashflow Sufficiency compares required future cash flows against projected available cash flows from the proposed investment structure to evaluate whether all required payments can be satisfied on time.',
        },
        {
          q: 'Can FP Structurer results be imported into FP Funds?',
          a: 'Yes. Solved FP Structurer allocations may be imported into FP Funds when supported by the applicable workflow.',
        },
        {
          q: 'Why might market values or yields change over time in FundPlanner?',
          a: 'Market values, yields and portfolio metrics may change due to updated market data, maturity roll-down, callable assumptions, valuation assumptions or refreshed security information.',
        },
      ],
    },
  ];

  return (
    <Page title="FAQs">
      <div className="space-y-6">
        {sections.map((section) => (
          <SectionCard
            key={section.title}
            title={section.title}
          >
            <div className="space-y-3">
              {section.items.map((f, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-app bg-app text-app p-5 shadow-sm"
                >
                  <div className="font-medium text-app">
                    {f.q}
                  </div>

                  <div className="text-sm text-muted mt-1">
                    {f.a}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        ))}
      </div>
    </Page>
  );
}